// Persist a LOCAL room-event bubble for the actor who just performed it
// (group rename / avatar change / member add / member remove, …).
//
// Why this exists: web renders its OWN outgoing messages optimistically and
// never re-renders its own server echo (self-echoes are reconciled, not
// re-emitted as new bubbles). Room events, however, were sent with NO optimistic
// row — so the actor, unlike every other member, never saw the event they just
// triggered. This mirrors iOS `MessagingService.emitRoomEvent`, which appends a
// local echo for the actor.
//
// The row is shaped exactly like the one peers persist on receive in
// `useIncomingMessages` (the chat-room variant dispatcher derives the event
// bubble from the `body` envelope JSON), and it carries the REAL `contentHash`
// returned by `conv.send` so that if the server ever does deliver the actor's
// echo, the Dexie `&contentHash` unique index dedupes it instead of double-
// rendering.

import { log } from '@core/log/log';

import { webMessagesPersister } from './persistenceAdapters';

function bytesToHex(bytes: Uint8Array | readonly number[]): string {
  let hex = '';
  for (const b of bytes) hex += (b & 0xff).toString(16).padStart(2, '0');
  return hex;
}

export async function persistLocalRoomEvent(params: {
  conversationId: string;
  senderAccountId: string;
  /** The `{ kind, payload }` app-event envelope that was sent via `conv.send`. */
  envelope: Record<string, unknown>;
  /** `content_hash` bytes from the `conv.send` result (real hash → echo dedupes). */
  contentHash: Uint8Array | readonly number[];
}): Promise<void> {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `local-evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const sentAt = Date.now();
  try {
    await webMessagesPersister.put({
      id,
      conversationId: params.conversationId,
      epoch: 0,
      serverSeq: sentAt,
      contentHash: bytesToHex(params.contentHash),
      senderAccountId: params.senderAccountId,
      // Same shape peers decode: the JSON app-event envelope drives the
      // event-bubble variant on the read side.
      body: JSON.stringify(params.envelope),
      sentAt,
      deliveryState: 'sent',
    });
  } catch (err) {
    // A `&contentHash` unique-index collision means the server echo already
    // landed and created the bubble — nothing to do.
    log.debug({
      event: 'chat.roomEvent.localBubble.skipped',
      conversationIdPrefix: params.conversationId.slice(0, 8),
      message: (err as Error).message,
    });
  }
}
